import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

import { fmtDate } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';

export default function CommercialAnalytics({ navigate }) {
  const isMobile = useIsMobile();
  const [funnel, setFunnel] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [repPerf, setRepPerf] = useState([]);
  const [sourcePerf, setSourcePerf] = useState(null);
  const [scoreboard, setScoreboard] = useState(null);
  const [dormant, setDormant] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [fRes, rRes, rpRes, spRes, sbRes, dRes, cRes] = await Promise.all([
        fetch(`${API}/api/sales/commercial/analytics/funnel`, authOpts()),
        fetch(`${API}/api/sales/commercial/analytics/revenue`, authOpts()),
        fetch(`${API}/api/sales/commercial/analytics/rep-performance`, authOpts()),
        fetch(`${API}/api/sales/commercial/analytics/source-performance`, authOpts()),
        fetch(`${API}/api/sales/commercial/analytics/repeat-scoreboard`, authOpts()),
        fetch(`${API}/api/sales/commercial/analytics/dormant`, authOpts()),
        fetch(`${API}/api/sales/commercial/config`, authOpts()),
      ]);
      if (fRes.ok) setFunnel(await fRes.json());
      if (rRes.ok) setRevenue(await rRes.json());
      if (rpRes.ok) setRepPerf(await rpRes.json());
      if (spRes.ok) setSourcePerf(await spRes.json());
      if (sbRes.ok) setScoreboard(await sbRes.json());
      if (dRes.ok) setDormant(await dRes.json());
      if (cRes.ok) setConfig(await cRes.json());
    } finally { setLoading(false); }
  };

  const exportCSV = async (type) => {
    window.open(`${API}/api/sales/commercial/export/${type}`, '_blank');
  };

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'funnel', label: 'Funnel' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'reps', label: 'Rep Performance' },
    { key: 'sources', label: 'Sources' },
    { key: 'dormant', label: `Dormant (${dormant.length})` },
    { key: 'config', label: 'Admin' },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading analytics...</div>;

  const kpiStyle = { background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '18px 22px', textAlign: 'center' };
  const kpiVal = { fontSize: isMobile ? 24 : 32, fontWeight: 800, color: '#1e293b', margin: 0 };
  const kpiLbl = { fontSize: 10, color: '#64748b', margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div data-testid="commercial-analytics" style={{ padding: isMobile ? '14px 10px' : '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 20, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#1e293b', margin: 0 }}>Commercial Analytics</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Performance metrics, funnel, and revenue tracking</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="export-accounts-csv" onClick={() => exportCSV('accounts')}
            style={{ padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
            Export Accounts
          </button>
          <button data-testid="export-bids-csv" onClick={() => exportCSV('bids')}
            style={{ padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
            Export Bids
          </button>
        </div>
      </div>

      {/* Repeat Contractor Scoreboard */}
      {scoreboard && (
        <div data-testid="repeat-scoreboard" style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 12, padding: isMobile ? '18px 16px' : '24px 32px',
          marginBottom: 20, color: '#fff', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 8px', color: '#94a3b8' }}>
            Primary Goal — Repeat Contractor Accounts
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: isMobile ? 40 : 56, fontWeight: 900, lineHeight: 1 }}>{scoreboard.current}</span>
            <span style={{ fontSize: isMobile ? 20 : 28, fontWeight: 400, color: '#94a3b8' }}>/ {scoreboard.goal}</span>
          </div>
          {/* Progress bar */}
          <div style={{ marginTop: 12, height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(scoreboard.percentage, 100)}%`, height: '100%', background: scoreboard.percentage >= 100 ? '#16a34a' : '#f59e0b', borderRadius: 99, transition: 'width 0.6s' }} />
          </div>
          <p style={{ fontSize: 12, margin: '6px 0 0', color: '#94a3b8' }}>
            {scoreboard.percentage}% of goal{scoreboard.percentage >= 100 ? ' — GOAL REACHED!' : ''}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, overflowX: 'auto', marginBottom: 16, borderBottom: '2px solid #e2e8f0', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`analytics-tab-${t.key}`} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 14px', fontSize: 12, fontWeight: tab === t.key ? 700 : 500, border: 'none',
              borderBottom: tab === t.key ? '2px solid #1e293b' : '2px solid transparent',
              color: tab === t.key ? '#1e293b' : '#64748b', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 8 : 12, marginBottom: 20 }}>
            {funnel && <>
              <div style={kpiStyle}><p style={kpiVal}>{funnel.total_accounts}</p><p style={kpiLbl}>Total Accounts</p></div>
              <div style={kpiStyle}><p style={kpiVal}>{funnel.bids_active}</p><p style={kpiLbl}>Active Bids</p></div>
              <div style={kpiStyle}><p style={kpiVal}>{funnel.bid_conversion_pct}%</p><p style={kpiLbl}>Bid Win Rate</p></div>
              <div style={kpiStyle}><p style={kpiVal}>{funnel.repeat_accounts}</p><p style={kpiLbl}>Repeat Accounts</p></div>
            </>}
          </div>
          {revenue && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ ...kpiStyle, borderColor: '#3b82f6' }}>
                <p style={{ ...kpiVal, fontSize: isMobile ? 20 : 26 }}>{money(revenue.commercial_quoted_revenue)}</p>
                <p style={kpiLbl}>Total Quoted Revenue</p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>{revenue.quoted_bid_count} bids — Avg {money(revenue.avg_bid_size)}</p>
              </div>
              <div style={{ ...kpiStyle, borderColor: '#16a34a' }}>
                <p style={{ ...kpiVal, fontSize: isMobile ? 20 : 26, color: '#16a34a' }}>{money(revenue.commercial_won_revenue)}</p>
                <p style={kpiLbl}>Total Won Revenue</p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>{revenue.won_bid_count} bids — Avg {money(revenue.avg_award_size)}</p>
              </div>
              <div style={{ ...kpiStyle, borderColor: '#f59e0b' }}>
                <p style={{ ...kpiVal, fontSize: isMobile ? 20 : 26, color: '#f59e0b' }}>{money(revenue.open_pipeline)}</p>
                <p style={kpiLbl}>Open Pipeline</p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>Lifetime Quoted: {money(revenue.lifetime_account_quoted)}</p>
              </div>
            </div>
          )}
          {funnel && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Conversion Rates</h3>
                <ConversionBar label="First Job Conversion" value={funnel.first_job_conversion_pct} color="#3b82f6" />
                <ConversionBar label="Bid Win Rate" value={funnel.bid_conversion_pct} color="#16a34a" />
                <ConversionBar label="Repeat Account Rate" value={funnel.repeat_conversion_pct} color="#f59e0b" />
              </div>
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Bid Pipeline</h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#475569' }}>Total Bids</span><span style={{ fontSize: 13, fontWeight: 700 }}>{funnel.total_bids}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#475569' }}>Active</span><span style={{ fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>{funnel.bids_active}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#475569' }}>Submitted</span><span style={{ fontSize: 13, fontWeight: 700, color: '#14b8a6' }}>{funnel.bids_submitted}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#475569' }}>Awarded</span><span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{funnel.bids_awarded}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#475569' }}>Lost</span><span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>{funnel.bids_lost}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Funnel Tab */}
      {tab === 'funnel' && funnel && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Commercial Account Funnel</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(funnel.stage_counts).map(([stage, count]) => {
              const pct = funnel.total_accounts > 0 ? (count / funnel.total_accounts * 100) : 0;
              return (
                <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12, color: '#475569', width: isMobile ? 120 : 180, flexShrink: 0, textTransform: 'capitalize' }}>
                    {stage.replace(/_/g, ' ')}
                  </span>
                  <div style={{ flex: 1, height: 24, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', background: count > 0 ? '#3b82f6' : '#e2e8f0', borderRadius: 6, transition: 'width 0.4s' }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', minWidth: 30, textAlign: 'right' }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {tab === 'revenue' && revenue && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Revenue Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Quoted Revenue', value: revenue.commercial_quoted_revenue, color: '#3b82f6', sub: `${revenue.quoted_bid_count} bids` },
              { label: 'Won Revenue', value: revenue.commercial_won_revenue, color: '#16a34a', sub: `${revenue.won_bid_count} bids` },
              { label: 'Avg Bid Size', value: revenue.avg_bid_size, color: '#6366f1', sub: 'per submitted bid' },
              { label: 'Avg Award Size', value: revenue.avg_award_size, color: '#14b8a6', sub: 'per won bid' },
              { label: 'Lifetime Account Quoted', value: revenue.lifetime_account_quoted, color: '#8b5cf6', sub: 'all accounts total' },
              { label: 'Lifetime Account Won', value: revenue.lifetime_account_won, color: '#16a34a', sub: 'all accounts total' },
              { label: 'Open Pipeline', value: revenue.open_pipeline, color: '#f59e0b', sub: 'pending opportunities' },
            ].map((m, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 18px', borderLeft: `4px solid ${m.color}` }}>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, textTransform: 'uppercase' }}>{m.label}</p>
                <p style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '4px 0 0' }}>{money(m.value)}</p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>{m.sub}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rep Performance Tab */}
      {tab === 'reps' && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Sales Rep Performance</h3>
          {repPerf.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>No rep data available.</p>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {repPerf.map(rep => (
                <div key={rep.user_id} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px' }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>{rep.name}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div><span style={{ fontSize: 10, color: '#94a3b8' }}>Accounts</span><p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{rep.accounts}</p></div>
                    <div><span style={{ fontSize: 10, color: '#94a3b8' }}>Win Rate</span><p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{rep.win_rate}%</p></div>
                    <div><span style={{ fontSize: 10, color: '#94a3b8' }}>Awarded</span><p style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#16a34a' }}>{money(rep.awarded_revenue)}</p></div>
                    <div><span style={{ fontSize: 10, color: '#94a3b8' }}>Repeat</span><p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{rep.repeat_accounts}</p></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Rep', 'Accounts', 'Bids', 'Won', 'Lost', 'Win Rate', 'Revenue', 'Repeat'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {repPerf.map(rep => (
                    <tr key={rep.user_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{rep.name}</td>
                      <td style={{ padding: '10px 14px' }}>{rep.accounts}</td>
                      <td style={{ padding: '10px 14px' }}>{rep.total_bids}</td>
                      <td style={{ padding: '10px 14px', color: '#16a34a', fontWeight: 600 }}>{rep.bids_awarded}</td>
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{rep.bids_lost}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700 }}>{rep.win_rate}%</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#16a34a' }}>{money(rep.awarded_revenue)}</td>
                      <td style={{ padding: '10px 14px' }}>{rep.repeat_accounts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sources Tab */}
      {tab === 'sources' && sourcePerf && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Performance by Source & Type</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 10px' }}>By Account Type</h4>
              {sourcePerf.by_account_type.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                  <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 500, textTransform: 'capitalize' }}>{(t.type || 'Unknown').replace(/_/g, ' ')}</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{t.count} accounts</span>
                    <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 8 }}>{money(t.won)} won</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 10px' }}>By Lead Source</h4>
              {sourcePerf.by_lead_source.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                  <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 500, textTransform: 'capitalize' }}>{(s.source || 'Unknown').replace(/_/g, ' ')}</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{s.count} accounts</span>
                    <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 8 }}>{money(s.won)} won</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dormant Tab */}
      {tab === 'dormant' && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Dormant Accounts</h3>
          {dormant.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              No dormant accounts. All accounts are active.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dormant.map(a => (
                <div key={a.id} onClick={() => navigate(`/sales/commercial/accounts/${a.id}`)}
                  style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', cursor: 'pointer', borderLeft: '4px solid #ef4444' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{a.company_name}</span>
                    {a.lifetime_won > 0 && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{money(a.lifetime_won)} won</span>}
                  </div>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
                    Last contact: {a.last_contact_at ? fmtDate(a.last_contact_at) : 'Never'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin Config Tab */}
      {tab === 'config' && (
        <ConfigPanel config={config} onSave={async (updates) => {
          await fetch(`${API}/api/sales/commercial/config`, authOpts({
            method: 'PUT', body: JSON.stringify(updates),
          }));
          loadAll();
        }} isMobile={isMobile} />
      )}
    </div>
  );
}

function ConversionBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#475569' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}%</span>
      </div>
      <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', background: color, borderRadius: 99 }} />
      </div>
    </div>
  );
}

function ConfigPanel({ config, onSave, isMobile }) {
  const [goal, setGoal] = useState(config?.repeat_account_goal || 10);
  const [dormantDays, setDormantDays] = useState(config?.dormant_threshold_days || 75);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    await onSave({ repeat_account_goal: parseInt(goal), dormant_threshold_days: parseInt(dormantDays) });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div data-testid="admin-config">
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Admin Configuration</h3>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '16px' : '24px', maxWidth: 500 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Repeat Contractor Accounts Goal</label>
          <input data-testid="config-repeat-goal" type="number" value={goal} onChange={e => setGoal(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, minHeight: 44, boxSizing: 'border-box' }} />
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>Primary scoreboard: REPEAT CONTRACTOR ACCOUNTS: X / {goal}</p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Dormant Account Threshold (days)</label>
          <input data-testid="config-dormant-days" type="number" value={dormantDays} onChange={e => setDormantDays(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, minHeight: 44, boxSizing: 'border-box' }} />
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>Accounts with no contact for {dormantDays}+ days flagged as dormant</p>
        </div>
        <button data-testid="save-config-btn" onClick={save}
          style={{ padding: '10px 24px', background: saved ? '#16a34a' : '#1e293b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
          {saved ? 'Saved!' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}
